import React, { useEffect, useState } from 'react';
import './blogPage.scss';
import PagesHeroSection from '../../components/pagesHeroSection/pagesHeroSection';
import emptyBlogImg from '../../assets/blog/no-blog-img.png';
import blogBg from '../../assets/blog/blogbg.jpeg';

import { Link, useParams } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import SEOUpdater, { CANONICAL_ORIGIN } from '../../utils/hoc/SEOUpdater';
import { useTranslation } from 'react-i18next';
import {
  getCachedIndex,
  loadBlogIndex,
  pickLang,
  blogPostPath,
  formatPostDate,
} from '../../utils/blogData';

/**
 * Post list comes from /blog-data/index.json (written at build time by
 * scripts/fetch-blog-data.js), preloaded before the first render on a direct
 * load - so react-snap prerenders the real list and the visitor's first
 * render matches it exactly. This replaces the old client-side Firestore
 * fetch + hand-written static list (scripts/inject-blog-content.js), which
 * never matched React's output and caused hydration error #418.
 */
const BlogPage = () => {
  const { t } = useTranslation('blogPage');
  const { lang } = useParams();

  const cached = getCachedIndex();
  const [blogs, setBlogs] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    loadBlogIndex()
      .then((list) => {
        if (!cancelled) setBlogs(list);
      })
      .catch((err) => {
        console.error('Error loading blogs:', err);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cached]);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Avagyan Law Firm Legal Blog',
    description:
      'Legal insights and updates covering immigration law, personal injury, lemon law, and employment law from Avagyan Law Firm.',
    url: `${CANONICAL_ORIGIN}/${lang}/blog/`,
    publisher: {
      '@type': 'Organization',
      name: 'Avagyan Law Firm',
    },
  };

  return (
    <div className="blog-page">
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <SEOUpdater page="blogPage" />

      <PagesHeroSection page={t('heroTitle')} background={blogBg} />

      <Container className="py-5">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">{t('loadingBlogs')}</p>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-5">
            <h2>{t('errorTitle')}</h2>
            <p>{t('errorDescription')}</p>
          </div>
        )}

        {!loading && !error && blogs.length === 0 && (
          <div className="text-center py-5">
            <h2>{t('noBlogs')}</h2>
            <p>{t('noBlogsDescription')}</p>
          </div>
        )}

        <Row>
          {blogs.map((blog) => {
            const title = pickLang(blog.title, lang) || 'Blog Article';
            const image = blog.imgUrl || emptyBlogImg;
            const href = blogPostPath(lang, blog.slug);
            const date = formatPostDate(blog.date);

            return (
              <Col lg={6} key={blog.slug} className="blog-card mb-4">
                <article className="blog-content">
                  <Row>
                    <Col sm={4} xs={5}>
                      <Link to={href} className="blog-image-wrapper">
                        <img src={image} alt={title} loading="lazy" />
                      </Link>
                    </Col>

                    <Col sm={8} xs={7}>
                      <h3 className="blog-title">
                        <Link to={href}>{title}</Link>
                      </h3>
                      {date && (
                        <p className="blog-date">
                          <time dateTime={blog.date}>{date}</time>
                        </p>
                      )}

                      <Link to={href} className="blog-read-more-btn">
                        {t('readMore')}
                      </Link>
                    </Col>
                  </Row>
                </article>
              </Col>
            );
          })}
        </Row>
      </Container>
    </div>
  );
};

export default BlogPage;
